<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { AppButton, AppTextInput, AppTypography } from '~/components/base';
import { useAutoHideScrollbar } from '~/composables/useAutoHideScrollbar';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';
import { useSuburbSearch } from '~/composables/useSuburbSearch';
import { useLocationStore } from '~/stores/location';
import type { Suburb } from '~/types/Location';

const visible = defineModel<boolean>('visible', { default: false });

const query = ref('');
// Deferred until the modal is actually opened — the endpoint returns every
// suburb in one unpaginated response, so there's no reason to pay for it on
// every page load when most visits never open this modal.
const { suburbs, pending, error } = useSuburbSearch(query, {
  enabled: visible,
});
const store = useLocationStore();

// First-time selection is framed as a task ("choose"); once a location is set
// the modal is only ever used to change it — the same on mobile and desktop.
//
// The longer heading embeds a \n before "Location": on the mobile
// full-screen sheet, the close button sits close enough to that single-line
// heading that "Location" can crowd it, so `.p-dialog-title`'s mobile
// `white-space: pre-line` (below) renders this as a forced line break there.
// Desktop's `white-space: nowrap` collapses it back into an ordinary space,
// so its heading stays one line. "Change Location" is short enough that it
// never had this problem, so it's left as-is.
// Kept as a plain string (not a custom `#header` slot) so PrimeVue's default
// title rendering — and the `aria-labelledby` id it attaches to it — is
// untouched.
const heading = computed(() =>
  store.isSet ? 'Change Location' : 'Choose Your Electorate\nLocation',
);

// The endpoint returns every suburb (~3000) in one unpaginated response, so
// the list is paged here rather than over the wire: render a page at a time
// and reveal the next as the user reaches the bottom. It used to render a
// flat first 20 with no way to reach the rest — most suburbs were
// unselectable.
const PAGE_SIZE = 20;
const visibleCount = ref(PAGE_SIZE);

const visibleSuburbs = computed(() =>
  suburbs.value.slice(0, visibleCount.value),
);
const hasMore = computed(() => visibleCount.value < suburbs.value.length);

function loadMore() {
  if (!hasMore.value) return;
  visibleCount.value += PAGE_SIZE;
}

// A new search is a new list — keep the reveal from carrying over, or the
// first page of the next query would arrive pre-scrolled.
watch(query, () => {
  visibleCount.value = PAGE_SIZE;
});

const sentinel = useTemplateRef<HTMLElement>('sentinel');
useInfiniteScroll(sentinel, loadMore);

const { isScrolling, onScroll: onListScroll } = useAutoHideScrollbar();

const isSelected = (s: Suburb) => store.location?.suburbId === s.id;

const select = (s: Suburb) => {
  store.setLocation({
    suburbId: s.id,
    suburb: s.suburb,
    postcodes: s.postcodes,
    electorates: s.electorates,
    region: s.region,
  });
  visible.value = false;
};

const removeLocation = () => {
  store.clearLocation();
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    position="top"
    :header="heading"
    class="app-modal location-picker w-full sm:max-w-[640px]"
    :dismissable-mask="true"
  >
    <div class="location-picker__content flex flex-col gap-4">
      <!-- Same 40px search-field pattern as the topbar: icon inside a single
           rounded field, borderless input (AppTextInput mounts its class onto
           the .p-inputtext itself, so it is styled via the wrapper). -->
      <div class="location-picker__search flex items-center gap-2">
        <Icon
          name="solar:magnifer-linear"
          class="location-picker__search-icon shrink-0"
        />
        <AppTextInput
          v-model="query"
          placeholder="Enter suburb or postcode"
          :floating-label="false"
          variant="outlined"
          size="small"
          enterkeyhint="search"
        />
      </div>

      <AppTypography type="caption-bold" class="text-color-tag uppercase">
        Suburbs
      </AppTypography>

      <ul
        class="location-picker__list flex flex-col"
        :class="{ 'location-picker__list--scrolling': isScrolling }"
        @scroll.passive="onListScroll"
      >
        <li
          data-testid="all-locations"
          class="location-picker__row location-picker__row--sticky flex cursor-pointer items-center justify-between pb-4"
          @click="removeLocation"
        >
          <AppTypography :type="store.isSet ? 'body' : 'body-bold'">
            All Locations
          </AppTypography>
          <svg
            v-if="!store.isSet"
            width="13"
            height="11"
            viewBox="0 0 13 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="location-picker__check"
            aria-hidden="true"
          >
            <path
              d="M0.75 6.15L3.89286 9.75L11.75 0.75"
              stroke="#201E1A"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </li>
        <li v-if="pending" class="py-6 text-center">
          <AppTypography type="subheadline" class="text-color-tertiary">
            Loading suburbs…
          </AppTypography>
        </li>
        <li v-else-if="error" class="py-6 text-center">
          <AppTypography type="subheadline" class="text-color-tertiary">
            Couldn't load suburbs. Please try again.
          </AppTypography>
        </li>
        <template v-else>
          <li
            v-for="s in visibleSuburbs"
            :key="s.id"
            :data-testid="`suburb-${s.suburb}`"
            class="location-picker__row flex cursor-pointer items-center justify-between py-4"
            @click="select(s)"
          >
            <AppTypography :type="isSelected(s) ? 'body-bold' : 'body'">
              {{ s.suburb }}
            </AppTypography>
            <svg
              v-if="isSelected(s)"
              width="13"
              height="11"
              viewBox="0 0 13 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="location-picker__check"
              aria-hidden="true"
            >
              <path
                d="M0.75 6.15L3.89286 9.75L11.75 0.75"
                stroke="#201E1A"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </li>
          <li v-if="!suburbs.length" class="py-6 text-center">
            <AppTypography type="subheadline" class="text-color-tertiary">
              No suburbs match "{{ query }}"
            </AppTypography>
          </li>
          <!-- Scrolled into view -> reveal the next page. Sits inside the
               scrollable list so it tracks the list's own scrolling, not the
               page behind the dialog. -->
          <li v-if="hasMore" ref="sentinel" aria-hidden="true"></li>
          <!-- Fallback, matching the list pages: where IntersectionObserver is
               missing or never fires, the composable stays silent — without
               this the picker would be stuck on its first page again. -->
          <li v-if="hasMore" class="flex justify-center py-2">
            <AppButton
              data-testid="show-more-suburbs"
              label="Show More"
              variant="text"
              size="small"
              @click="loadMore"
            />
          </li>
        </template>
      </ul>
    </div>
  </Dialog>
</template>

<!--
  The dialog is teleported to <body> and carries no scoped data-v attribute,
  so scoped :deep() can't reach its chrome (header/content/panel). The
  `.location-picker` class *is* on the .p-dialog panel, so the sheet chrome is
  themed in the unscoped block below; MY slot content (search/rows) keeps its
  scoped styles here.
-->
<style scoped lang="scss">
.location-picker {
  &__content {
    // PrimeVue's `.p-dialog-content` is itself a flex-grow:1 flex column, but
    // this wrapper div doesn't stretch into it by default — without `h-full`
    // here, `&__list`'s `flex: 1 1 auto` (mobile media query below) has
    // nothing to grow into and the list stays its natural content height.
    @media (max-width: 639.98px) {
      height: 100%;
    }
  }

  &__search {
    height: 40px;
    border-radius: 8px; // Figma: radius/sm — was a full pill (9999px)
    padding: 0 0.875rem;
    // White field standing out on the sand sheet (Figma).
    background-color: $surface-color-bg;

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.p-inputtext) {
      width: 100%;
      height: 38px;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;

      &::placeholder {
        color: #201e1a; // Figma: text/primary
      }

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }
    }
  }

  &__search-icon {
    font-size: 1.125rem;
    color: $text-color-tertiary;
  }

  &__list {
    // Reserve a fixed height so the dialog doesn't visibly shrink when a
    // search returns zero/few suburbs; scrolls internally once the visible
    // 20-result cap exceeds it. Clamped against
    // 50vh (not a bare 26rem) so short viewports (e.g. landscape phones)
    // can shrink the list and scroll instead of forcing the dialog to
    // overflow the viewport.
    min-height: min(26rem, 50vh);
    max-height: min(26rem, 50vh);
    overflow-y: auto;

    @include auto-hide-scrollbar;

    // On the mobile full-height sheet (see the `.location-picker.p-dialog`
    // media query), grow to fill the remaining space below the search bar
    // and "Suburbs" label instead of stopping at the fixed desktop clamp —
    // otherwise the scrollable area leaves dead space at the bottom of the
    // full-height dialog. `min-height: 0` is required for a flex child to
    // actually shrink/scroll instead of overflowing its flex container.
    @media (max-width: 639.98px) {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
      // Mobile's overlay scrollbar doesn't reserve track space like
      // desktop's does, so it drew over the flush-right check icon/border.
      padding-right: $spacing-sm;
    }
  }

  &__row {
    // Warm tan divider that reads on the sand sheet (Figma) instead of the
    // cool grey used on white surfaces.
    border-bottom: 1px solid $complementary-color-shade-1;

    &:last-of-type {
      border-bottom: none;
    }

    // "All Locations" stays pinned to the top of the scrolling list so the way
    // to clear a selection is always reachable. Needs its own opaque sheet
    // colour (rows are transparent over the dialog panel) or the suburbs would
    // scroll through underneath it.
    &--sticky {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: #ece3d1; // sheet colour, see .location-picker.p-dialog
    }
  }

  &__check {
    flex-shrink: 0;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.location-picker` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.location-picker.p-dialog {
  // Anchored via `position="top"` (not the Dialog default "center") so the
  // dialog's Y position stays fixed instead of recalculating whenever its
  // content height changes (e.g. the suburb list's loading/loaded states).
  // PrimeVue's own top-position defaults (1rem margin, 90% max-height) apply
  // above the `sm` breakpoint.
  // The square corners, viewport bound and the mobile full-screen sheet come
  // from the shared `.app-modal` chrome (assets/scss/components/_dialog.scss).
  overflow-y: auto;

  // Warm sand sheet to match Figma (replaces the default white dialog).
  // Header/content inherit this from the panel rather than painting their
  // own background — a separate opaque rectangle on either would otherwise
  // need its own corner-radius bookkeeping to avoid a seam.
  background-color: #ece3d1;

  // PrimeVue's own base dialog style sets `color: var(--p-dialog-color)`
  // (a cool slate) directly on `.p-dialog`, and every text node inside —
  // title, "Suburbs" label, suburb rows — inherits it since none of them set
  // their own `color`. Figma's `text/primary` for this modal is the warm
  // #201e1a used elsewhere on the sand sheet, not the app's default text
  // colour, so it's overridden once here rather than on every child.
  color: #201e1a;

  // Centered title with the close button pinned to the right, on mobile. A
  // 3-column grid with fixed, equal-width side columns (instead of
  // `justify-content: center`, which centers the title across the *full*
  // header width once the close button's actions wrapper collapses to 0
  // width) guarantees the title's box never extends into the button's
  // column, however long the text — it did for the longer "Choose Your
  // Electorate Location" heading shown before a location is set (VTW-476),
  // even though the short "Location"/"Change Location" headings happened to
  // leave enough clearance. $spacing-4xl matches the close button's own
  // rendered footprint (48px), so the button sits flush in its reserved
  // column.
  //
  // Desktop (>= 640px, matching this file's existing mobile-sheet cutoff at
  // 639.98px elsewhere) drops the centered treatment: Figma shows the title
  // flush-left there, with no left-side gutter to balance — that gutter was
  // only ever needed to keep a *centered* title off the button. Mobile keeps
  // the original centered layout untouched.
  .p-dialog-header {
    position: relative;

    @media (max-width: 639.98px) {
      justify-content: center;
    }
  }

  .p-dialog-title {
    // Theme tokens (`--p-dialog-title-font-weight/size`) render this as
    // semibold/30px-leading; Figma's Title 3 style for this heading is bold
    // with a tighter 24px leading.
    font-weight: 700;
    line-height: 24px;

    // Figma: 16px on the mobile full-screen sheet, 20px on desktop — sizes
    // used to be identical at every breakpoint because nothing here
    // overrode the (20px) theme default.
    font-size: 16px;

    // The longer heading embeds a \n before "Location" (see the `heading`
    // computed) to keep it clear of the close button on the full-screen
    // mobile sheet. `nowrap` (below, desktop-only) collapses that \n into an
    // ordinary space; `pre-line` here renders it as the intended line break
    // on mobile instead.
    @media (max-width: 639.98px) {
      text-align: center;
      white-space: pre-line;
      // Keeps the centered text clear of the close button pinned to the
      // corner (see `.p-dialog-close-button` below) regardless of line
      // length, without reserving a whole dedicated layout column for it.
      padding: 0 $spacing-3xl;
    }

    // Desktop: flush-left (PrimeVue's own default header layout), truncated
    // with an ellipsis rather than wrapping/overflowing past the close
    // button however long the text gets.
    @media (min-width: 640px) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 20px;
    }
  }

  // Bare X on the mobile full-screen sheet, and a persistent light circle
  // behind the X on desktop — matching every other filter/picker modal's
  // close-button treatment (Campaign/PartyFilterModal, CandidatePartyModal).
  .p-dialog-header .p-dialog-close-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    box-shadow: none;

    // Alignment follows the base `_dialog.scss` inset ($spacing-sm) on
    // mobile — the same offset every other modal's mobile close button
    // uses, so it lines up flush with the row content below.
    @media (max-width: 639.98px) {
      color: $text-color-subtext;
      background: transparent;
    }

    // Aligns the button's right edge with the row content's right edge,
    // matching the other filter modals' desktop treatment. `!important` is
    // required: the shared `.app-modal.app-modal.p-dialog .p-dialog-header
    // .p-dialog-close-button` rule in `_dialog.scss` sets `right:
    // $spacing-sm` at 5 class-selectors of specificity, which beats this
    // rule's 4 regardless of source order.
    //
    // PrimeVue's default close button only paints its light-grey circular
    // background on `:hover` — at rest it's transparent. Figma's desktop
    // reference shows that circle persistently, at rest, so it's pinned on
    // unconditionally here. PrimeVue's default footprint for this button is
    // 48x48px; Figma's is a 28px circle.
    @media (min-width: 640px) {
      right: $spacing-xl !important;
      width: 28px;
      height: 28px;
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }
  }
}
</style>
